import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from "@/lib/supabaseClient";
import {
  getSessionBounded,
  readPersistedSession,
  SESSION_BOOT_WATCHDOG_MS,
} from "@/lib/sessionBoot";
import {
  AUTH_NOTICE_KEY,
  decideSession,
  shouldSignOut,
  type AccountStatusRow,
} from "@/lib/accountStatus";
import { logUserActivity } from "@/utils/activityLogger";
import { useNavigate } from "react-router-dom";
import { getCurrentTenantId } from "@/utils/tenantUtils";

// Define return types for auth methods
type LoginResult = { user: User | null; session: Session | null } | null;
type OtpResult = { error: Error | null };

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  authLoading: boolean; // Add this for backward compatibility
  originalUser: User | null;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWithOtp: (email: string) => Promise<OtpResult>;
  logout: () => Promise<void>;
  impersonateUser: (user: User) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * SUB-1.35 · F-64. Sign out an identity whose account has been deactivated.
 *
 * ⚠️ **ONE PLACE, BECAUSE THERE ARE THREE WAYS IN.** Password login, OTP magic
 * link and session restore all establish a session, and only the first goes
 * through `login()`. A gate there would have covered one of the three, which is
 * the kind of partial fix that reads as done. This runs wherever a session
 * appears.
 *
 * ⚠️ **THIS IS NOT THE REVOCATION.** It runs in the browser; a deactivated user
 * holding a refresh token can call PostgREST directly and never execute it.
 * `lib/accountStatus.ts` has the argument and names the three server-side
 * changes that are the actual fix.
 *
 * ⚠️ **IT READS THE USER'S OWN ROW, WHICH RLS ALWAYS PERMITS.** `users_select`
 * carries an `id = auth.uid()` arm, so this needs no new function and no new
 * grant. Adding a SECURITY DEFINER helper for it would have created exactly the
 * directly-callable surface SUB-1.28 exists to remove 47 of.
 */
async function enforceAccountActive(userId: string): Promise<boolean> {
  let row: AccountStatusRow | null = null;
  try {
    const { data, error } = await supabase
      .from("users")
      .select("is_active")
      .eq("id", userId)
      .maybeSingle();
    // ⚠️ An error leaves `row` null, which decides ALLOWED. Fail open: see
    // `accountStatus.ts`. A UX gate that can lock out the platform on a slow
    // query is a worse trade than one that occasionally lets a deactivated
    // account reach a page the policies will show nothing on.
    if (!error) row = (data as AccountStatusRow | null) ?? null;
  } catch (statusError) {
    console.error("Account status check failed, allowing the session:", statusError);
  }

  const decision = decideSession(row);
  if (!shouldSignOut(decision)) return true;

  // ⚠️ LEAVE THE REASON BEHIND BEFORE SIGNING OUT. Without this the user is
  // bounced to the login screen with no explanation and tries the same password
  // again, which looks like a broken product rather than a closed account.
  // sessionStorage, not localStorage: it is a one-time notice about this tab's
  // sign-out, not a preference to remember.
  try {
    sessionStorage.setItem(AUTH_NOTICE_KEY, decision.reason);
  } catch {
    // Private browsing or a storage quota. The sign-out still has to happen.
  }

  await supabase.auth.signOut();
  return false;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true);
        /**
         * ⚠️ BOUNDED ON PURPOSE. This await gates the entire application: `ProtectedRoute`
         * renders a bare "Loading..." while `loading` is true, and `loading` is only cleared
         * in the `finally` below. `getSession()` waits on a Web Lock with no bound of its
         * own, so a lock that is never released used to leave every route on that spinner
         * until the tab was manually reloaded. See sessionBoot.ts for the fallback and why
         * it keeps the user signed in rather than bouncing them to /login.
         */
        const { session, error } = await getSessionBounded({
          getSession: () => supabase.auth.getSession(),
          storage: typeof window !== 'undefined' ? window.localStorage : undefined,
          storageKey: SUPABASE_AUTH_STORAGE_KEY,
          onTimeout: (timeoutMs) => {
            console.warn(
              `[auth] getSession() did not answer within ${timeoutMs}ms; using the persisted ` +
                'session so the app cannot hang on "Loading...". See sessionBoot.ts.'
            );
          },
        });
        if (error) {
          console.error("Session fetch error:", error);
          // Don't set loading to false immediately on error, retry once
          setTimeout(async () => {
            try {
              const {
                data: { session: retrySession },
                error: retryError,
              } = await supabase.auth.getSession();
              if (retryError) {
                console.error("Session fetch retry error:", retryError);
                setLoading(false);
                return;
              }
              setSession(retrySession);
              setUser(retrySession?.user ?? null);
            } catch (retryCatchError) {
              console.error(
                "Session fetch retry catch error:",
                retryCatchError
              );
            } finally {
              setLoading(false);
            }
          }, 1000);
          return;
        }

        // Check for impersonation state in localStorage
        const storedImpersonation = localStorage.getItem("impersonationState");

        if (storedImpersonation && !isImpersonating) {
          const { originalUser: storedOriginalUser, impersonatedUser } =
            JSON.parse(storedImpersonation);

          setOriginalUser(storedOriginalUser);
          setUser(impersonatedUser);
          setSession(session);
          setIsImpersonating(true);
        } else if (!storedImpersonation) {
          // SUB-1.35 · F-64. A restored session is a session, so it is checked
          // like any other. Without this, deactivating somebody would take
          // effect only on their next fresh login, which may never come.
          if (session?.user && !(await enforceAccountActive(session.user.id))) {
            setSession(null);
            setUser(null);
            return;
          }
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error: unknown) {
        console.error("Session fetch error:", error);
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };

    /**
     * ⚠️ THE WATCHDOG IS THE GUARANTEE, not `fetchSession`'s `finally`.
     *
     * That `finally` only runs once every await above it has settled, and every one of them
     * — `getSession()`, `enforceAccountActive()` — waits on the auth lock. Measured with the
     * lock held: bounding `getSession()` alone was not enough and the app still never
     * rendered. This clears the gate no matter which await is stuck, and adopts the persisted
     * session on the way out so a stall does not sign the user out and lose their place.
     *
     * `prev ?? persisted` rather than a plain assignment: if fetchSession finished normally
     * in the meantime, its answer is authoritative and must not be overwritten by storage.
     */
    const budgetMs = SESSION_BOOT_WATCHDOG_MS;
    const watchdog = setTimeout(() => {
      const persisted = readPersistedSession(
        typeof window !== "undefined" ? window.localStorage : undefined,
        SUPABASE_AUTH_STORAGE_KEY
      );
      console.warn(
        `[auth] session boot did not finish within ${budgetMs}ms; rendering ` +
          "with the persisted session so the app cannot hang on \"Loading...\". See sessionBoot.ts."
      );
      setSession((prev) => prev ?? persisted);
      setUser((prev) => prev ?? persisted?.user ?? null);
      setLoading(false);
    }, budgetMs);

    fetchSession().finally(() => clearTimeout(watchdog));

    /**
     * ⚠️ THIS CALLBACK IS SYNCHRONOUS ON PURPOSE, AND MUST NEVER `await` A `supabase.*`
     * CALL. Do not make it `async` again.
     *
     * auth-js invokes its subscribers while it is HOLDING the auth Web Lock
     * (`lock:<storageKey>`, so `lock:solerro-pm-auth` here). Two consequences, and the
     * second is what made this severe:
     *
     *   1. The lock is not reentrant. Awaiting another Supabase call from inside the
     *      handler re-acquires it from a chain that already holds it, and holds it across
     *      a network round trip rather than for the microtask it was designed for.
     *   2. Web Locks are scoped to the ORIGIN, shared by every tab. One wedged tab
     *      therefore blocks auth in all of them, which is why this presented as "having
     *      several tabs open breaks the app" when tab count was never the cause.
     *
     * That is what SUB-1.35's gate did from 2026-08-16 until this commit: it awaited
     * `enforceAccountActive`, which awaits `from("users")` and can await `auth.signOut()`.
     * The symptom was not a slow request, it was a request that never issued at all:
     * `supabase.rpc()` resolves the session before it calls `fetch`, so a caller's
     * `finally` never ran and its button sat on a spinner indefinitely. A client account
     * creation was reported stuck for 30 minutes with no matching row in the API logs.
     *
     * Returning a promise is what keeps the lock held, so `async` is the defect even
     * before any specific await. Everything that touches Supabase goes through the
     * deferral below, which runs on a fresh task after the lock has been released.
     */
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // SUB-1.35 · F-64. This is the path OTP and magic-link sign-ins arrive
        // on, so it is the one a `login()`-only gate would have missed.
        //
        // ⚠️ SIGNED_OUT carries no user and must not be checked: calling
        // `signOut()` from inside the handler for its own event is a loop.
        //
        // ⚠️ THE IMPERSONATION GUARD READS localStorage, NOT `isImpersonating`.
        // This effect has an empty dependency array, so the closure captures
        // `isImpersonating` as its initial `false` forever. Trusting it would
        // check the IMPERSONATED user's status and, if they are deactivated,
        // sign the superadmin out of their own session. `fetchSession` above
        // reads the same key for the same reason.
        const impersonating = (() => {
          try {
            return localStorage.getItem("impersonationState") !== null;
          } catch {
            return false;
          }
        })();
        if (!impersonating && session?.user && event !== "SIGNED_OUT") {
          // Deferred, not awaited. `setTimeout(0)` yields to the task queue, which is
          // after auth-js has released the lock around this notification.
          //
          // ⚠️ THE CHECK NOW RUNS AFTER THE STATE UPDATES BELOW RATHER THAN BEFORE THEM,
          // so a deactivated user is briefly in state before being cleared. That is the
          // cost of not holding the lock and it is the right trade: the gate was never
          // the revocation (see the header on `enforceAccountActive`), the server-side
          // policies are, and a few milliseconds of optimistic state cannot reach data
          // the policies would not have handed over anyway.
          const gatedUserId = session.user.id;
          setTimeout(() => {
            void enforceAccountActive(gatedUserId).then((active) => {
              if (!active) {
                setSession(null);
                setUser(null);
              }
            });
          }, 0);
        }
        if (!isImpersonating) {
          // Only update state if there's actually a change to prevent unnecessary re-renders
          setSession(prevSession => {
            // Compare session properties to avoid unnecessary updates
            if (!prevSession && !session) return prevSession;
            if (!prevSession && session) return session;
            if (prevSession && !session) return session;
            if (prevSession?.user?.id === session?.user?.id && 
                prevSession?.access_token === session?.access_token &&
                prevSession?.expires_at === session?.expires_at) {
              return prevSession; // No change, prevent re-render
            }
            return session;
          });
          
          setUser(prevUser => {
            const newUser = session?.user ?? null;
            // Don't check avatar_url to avoid unnecessary re-renders
            // The Header component will handle avatar updates via realtime subscription
            
            if (prevUser?.id === newUser?.id && 
                prevUser?.email === newUser?.email) {
              return prevUser; // No change, prevent re-render
            }
            return newUser;
          });
        }
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(watchdog);
      authListener?.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove isImpersonating dependency to prevent infinite loop

  const login = async (
    email: string,
    password: string
  ): Promise<LoginResult> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Log successful login
      if (data.user) {
        await logUserActivity.login(data.user.id);
      }

      return data.user && data.session
        ? { user: data.user, session: data.session }
        : null;
    } catch (error: unknown) {
      console.error("Login error:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loginWithOtp = async (email: string): Promise<OtpResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) return { error };
      alert("Check your email for the login link!");
      return { error: null };
    } catch (error: unknown) {
      return {
        error:
          error instanceof Error ? error : new Error("Unknown error occurred"),
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Log logout before signing out
      if (user) {
        await logUserActivity.logout(user.id);
      }

      localStorage.removeItem("impersonationState");
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setOriginalUser(null);
      setIsImpersonating(false);
    } catch (error: unknown) {
      console.error("Error during logout:", error);
    } finally {
      setLoading(false);
    }
  };

  const impersonateUser = async (userToImpersonate: User) => {
    try {
      // Store current user as original user
      const originalUser = user;
      if (!originalUser) {
        throw new Error('No current user found for impersonation');
      }
      setOriginalUser(originalUser);
      
      // Get current session token
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        throw new Error("No active session found");
      }

      // Call the impersonate-user Edge Function
      console.log('Calling impersonate function with:', {
        targetUserId: userToImpersonate.id,
        action: 'start'
      });

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { 
          targetUserId: userToImpersonate.id,
          action: 'start'
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        }
      });

      console.log('Function response:', { data, error });

      if (error) {
        console.error("Error calling impersonate function:", error);
        throw new Error(`Function error: ${error.message || 'Failed to start impersonation'}`);
      }

      if (!data) {
        throw new Error('No data returned from function');
      }

      if (!data.success) {
        console.error("Function returned error:", data);
        throw new Error(`Function error: ${data.error || 'Impersonation failed'}`);
      }

      // Store original session for restoration later
      localStorage.setItem("originalSession", JSON.stringify(currentSession));
      localStorage.setItem("impersonationState", JSON.stringify({
        originalUser,
        impersonatedUser: data.session.user
      }));

      // Store original session and user info for logging
      const originalTenantId = await getCurrentTenantId();
      
      // Set the new session using Supabase's setSession
      const { error: setSessionError } = await supabase.auth.setSession(data.session);
      
      if (setSessionError) {
        console.error("Error setting impersonation session:", setSessionError);
        throw setSessionError;
      }

      // Update state first
      setIsImpersonating(true);
      setUser(data.session.user);

      // Log impersonation start after impersonation is set up - using explicit values to avoid session confusion
      try {
        console.log(`[Impersonation] Logging impersonation start: ${originalUser.email || 'unknown'} -> ${data.session.user.email || 'unknown'}`);
        await logUserActivity.impersonationStarted(
          data.session.user.id,
          data.session.user.email || '',
          originalUser.id,
          originalUser.email || '',
          originalTenantId || undefined
        );
        console.log(`[Impersonation] Successfully logged impersonation start`);
      } catch (logError) {
        console.error("Error logging impersonation start:", logError);
        // Don't fail the impersonation if logging fails
      }

      console.log(`Started impersonating user: ${data.session.user.email}`);

      // Redirect to dashboard
      navigate("/app/dashboard");
    } catch (error) {
      console.error("Error impersonating user:", error);
      // Reset impersonation state on error
      setOriginalUser(null);
      setIsImpersonating(false);
      localStorage.removeItem("impersonationState");
      localStorage.removeItem("originalSession");
      throw error;
    }
  };

  const stopImpersonating = async () => {
    try {
      const impersonationState = localStorage.getItem("impersonationState");
      const originalSessionData = localStorage.getItem("originalSession");
      
      if (!impersonationState || !originalSessionData) {
        throw new Error("No impersonation state found");
      }

      const { originalUser } = JSON.parse(impersonationState);
      const originalSession = JSON.parse(originalSessionData);
      
      // Get current impersonated user for logging
      const currentImpersonatedUser = user;

      // Restore the original session
      const { error: setSessionError } = await supabase.auth.setSession(originalSession);
      
      if (setSessionError) {
        console.error("Error restoring original session:", setSessionError);
        throw setSessionError;
      }

      // Log impersonation end - now we're back to original session
      if (currentImpersonatedUser) {
        try {
          console.log(`[Impersonation] Logging impersonation end: ${originalUser.email || 'unknown'} <- ${currentImpersonatedUser.email || 'unknown'}`);
          const tenantId = await getCurrentTenantId();
          await logUserActivity.impersonationEnded(
            currentImpersonatedUser.id,
            currentImpersonatedUser.email || '',
            originalUser.id,
            originalUser.email || '',
            tenantId || undefined
          );
          console.log(`[Impersonation] Successfully logged impersonation end`);
        } catch (logError) {
          console.error("Error logging impersonation end:", logError);
          // Don't fail the restoration if logging fails
        }
      }

      // Clear impersonation state
      localStorage.removeItem("impersonationState");
      localStorage.removeItem("originalSession");
      setOriginalUser(null);
      setIsImpersonating(false);

      // Set the user state back to original user
      setUser(originalUser);

      console.log(`Stopped impersonating, restored session for: ${originalUser.email}`);

      // Redirect to settings page
      navigate("/app/settings");
    } catch (error) {
      console.error("Error stopping impersonation:", error);
      throw error;
    }
  };

  const value = useMemo(() => ({
    session,
    user,
    loading,
    authLoading: loading, // Add this for backward compatibility
    originalUser,
    isImpersonating,
    login,
    loginWithOtp,
    logout,
    impersonateUser,
    stopImpersonating,
  }), [session, user, loading, originalUser, isImpersonating, login, loginWithOtp, logout, impersonateUser, stopImpersonating]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
